import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import MarkdownLink from "../components/MarkdownLink";

interface Article {
  id: number;
  section: string;
  sectionTitle: string;
  slug: string;
  title: string;
  content: string;
}

export default function HelpArticle() {
  const { section, slug } = useParams<{ section: string; slug: string }>();
  const { getAccessTokenSilently } = useAuth0();
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [showComment, setShowComment] = useState(false);
  const [comment, setComment] = useState("");

  useEffect(() => {
    async function loadArticle() {
      setLoading(true);
      setError(null);
      setFeedbackSent(false);
      setShowComment(false);
      setComment("");

      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(
          `${process.env.REACT_APP_API_ROOT}/help/articles/${section}/${slug}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        if (!response.ok) {
          setError(
            response.status === 404
              ? "Article not found."
              : `Failed to load article: ${response.statusText}`,
          );
          setLoading(false);
          return;
        }

        const data = await response.json();
        setArticle(data.data);
        setLoading(false);
      } catch (err) {
        setError(`Failed to load: ${err}`);
        setLoading(false);
      }
    }

    if (section && slug) {
      loadArticle();
    }
  }, [section, slug, getAccessTokenSilently]);

  async function submitFeedback(helpful: boolean, feedbackComment?: string) {
    if (!article) return;
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${process.env.REACT_APP_API_ROOT}/help/feedback`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            articleId: article.id,
            helpful,
            ...(feedbackComment ? { comment: feedbackComment } : {}),
          }),
        },
      );
      if (response.ok) setFeedbackSent(true);
    } catch {
      // A refused or unanswered feedback keeps the controls and the typed text
    }
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  if (!article) return null;

  return (
    <article className="help-article">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{ a: MarkdownLink }}
      >
        {article.content}
      </ReactMarkdown>

      <hr className="my-4" />
      <div className="help-feedback">
        {feedbackSent ? (
          <p className="text-muted">Thanks for your feedback!</p>
        ) : showComment ? (
          <>
            <p className="text-muted mb-2">How can we improve this article?</p>
            <textarea
              className="form-control form-control-sm mb-2"
              rows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Optional: tell us what was missing or unclear..."
            />
            <button
              className="btn btn-outline-primary btn-sm me-2"
              onClick={() => submitFeedback(false, comment || undefined)}
            >
              Submit
            </button>
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => submitFeedback(false)}
            >
              Skip
            </button>
          </>
        ) : (
          <>
            <p className="text-muted mb-2">Was this article helpful?</p>
            <button
              className="btn btn-outline-success btn-sm me-2"
              onClick={() => submitFeedback(true)}
            >
              <i className="bi bi-hand-thumbs-up" /> Yes
            </button>
            <button
              className="btn btn-outline-danger btn-sm"
              onClick={() => setShowComment(true)}
            >
              <i className="bi bi-hand-thumbs-down" /> No
            </button>
          </>
        )}
      </div>
    </article>
  );
}
